import { formatNiiSignoffTimestamp } from 'features/libra/stepComponents/niiReport/content/buildNiiReviewContent'
import {
  formatOverriddenBy,
  type NiiOverrideDetail,
} from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import { ZONE_COLORS } from 'features/libra/stepComponents/niiReport/zoneUtils'
import {
  calculateRequiredCoverage,
  type ConsequenceOfFailure,
  type CoverageCalculation,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  getTypeBLimits,
  InspectionStrategy,
  niiPathReaches,
  type NiiStrategy,
  type RbaInformation,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { buildZoneOverviewRow } from 'features/libra/stepComponents/zoneAnalysis/useComputedZoneData'
import {
  getEffectiveZoneHeight,
  getEffectiveZoneLength,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  COVERAGE_COLUMNS,
  ZONE_DETAIL_FIELDS,
} from 'features/libra/stepComponents/zoneAnalysis/zoneDetailConstants'
import {
  formatStoredZoneOverrideValue,
  listZoneOverrides,
} from 'features/libra/stepComponents/zoneAnalysis/zoneOverrides'
import type {
  PdfTableData,
  PdfTableRow,
} from 'features/reporting/pdfGeneration/components/shared/PdfTable'

const STRATEGY_LABELS: Record<string, string> = {
  A: 'Type A — Confirmation Strategy',
  B: 'Type B — Quantitative (Predictable)',
  C: 'Type C — Quantitative (Unpredictable)',
}

export interface NiiCoverageBreakdown {
  title: string
  resultPercent: string
  resultSubtext: string
  formulaLines: ReadonlyArray<string>
  modifiers: ReadonlyArray<string>
}

export interface NiiZoneDetail {
  title: string
  color: string
  zoneIndex: number
  damageMechanisms?: PdfTableData
  designAndFunction?: PdfTableData
  strategy?: {
    table: PdfTableData
    overallDecision?: string
  }
  rba?: PdfTableData
  coverageRequirements?: PdfTableData
  coverageBreakdown?: NiiCoverageBreakdown
  overrides?: ReadonlyArray<NiiOverrideDetail>
}

export interface NiiZoneAnalysis {
  zoneBreakdownJustification?: string
  overview?: PdfTableData
  zones: ReadonlyArray<NiiZoneDetail>
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Prefer the persisted color painted by drawZonesOnCanvas so report dots match
 * the annotated drawing. The palette fallback uses the full zone array index,
 * not the position in a filtered list of zones shown on one image.
 */
export function resolveZoneColor(zone: ZoneParams, zoneIndex: number): string {
  return zone.color || ZONE_COLORS[zoneIndex % ZONE_COLORS.length]
}

export function buildDamageMechanismsTable(
  zone: ZoneParams
): PdfTableData | undefined {
  const mechanisms = zone.damage_mechanisms ?? []
  if (mechanisms.length === 0) return undefined

  return {
    headers: [
      'Damage Mechanism',
      'Theoretical Rate/Susceptibility',
      'Justification',
    ],
    columnWidths: [0.28, 0.28, 0.44],
    rows: mechanisms.map((dm) => ({
      cells: [
        dm.name,
        dm.rate_value !== undefined
          ? `${dm.rate_value}${dm.rate_unit ?? ''}`
          : (dm.susceptibility ?? '—'),
        dm.justification ?? '—',
      ],
    })),
  }
}

export function buildDesignAndFunctionTable(
  zone: ZoneParams
): PdfTableData | undefined {
  const computedAccessors: Record<string, () => string | number | undefined> = {
    effectiveLength: () => getEffectiveZoneLength(zone),
    effectiveHeight: () => getEffectiveZoneHeight(zone),
    effectiveArea: () => {
      const a = getEffectiveZoneArea(zone)
      return a > 0 ? a.toFixed(2) : undefined
    },
  }

  const rows: PdfTableRow[] = []
  for (const entry of ZONE_DETAIL_FIELDS) {
    const value =
      'resolve' in entry
        ? computedAccessors[entry.resolve]?.()
        : (zone as unknown as Record<string, string | number | undefined>)[
            entry.field
          ]
    if (value !== undefined && value !== null && value !== '') {
      rows.push({ cells: [entry.label, String(value)] })
    }
  }

  if (rows.length === 0) return undefined

  return {
    headers: ['Parameter', 'Value'],
    columnWidths: [0.55, 0.45],
    rows,
  }
}

function buildStrategySection(strategy: NiiStrategy | undefined):
  | {
      table: PdfTableData
      overallDecision?: string
    }
  | undefined {
  if (!strategy) return undefined

  const reached = niiPathReaches(strategy)
  const rows: PdfTableRow[] = []

  if (strategy.degradation_likely !== undefined) {
    rows.push({
      cells: [
        `Degradation likely/measurable within two inspection intervals?\n${strategy.degradation_likely ? 'Yes' : 'No'}`,
        strategy.degradation_likely_justification ??
          strategy.justification ??
          '—',
      ],
    })
  }

  if (reached.predictable && strategy.degradation_predictable !== undefined) {
    rows.push({
      cells: [
        `Degradation Predictable?\n${strategy.degradation_predictable ? 'Yes' : 'No'}`,
        strategy.degradation_predictable_justification ??
          strategy.justification ??
          '—',
      ],
    })
  }

  if (reached.severity && strategy.degradation_severity !== undefined) {
    const isLowMedium =
      strategy.degradation_severity === 'low' ||
      strategy.degradation_severity === 'medium'
    rows.push({
      cells: [
        `Degradation Low/Medium?\n${isLowMedium ? 'Yes' : 'No'}`,
        strategy.degradation_severity_justification ??
          strategy.justification ??
          '—',
      ],
    })
  }

  if (rows.length === 0) return undefined

  const effectiveType = getEffectiveStrategyType(strategy)
  const overallDecision = effectiveType
    ? `${STRATEGY_LABELS[effectiveType] ?? effectiveType}${
        strategy.strategy_type_override ? ' (Overridden)' : ''
      }`
    : undefined

  return {
    table: {
      headers: ['Question', 'Justification'],
      columnWidths: [0.45, 0.55],
      rows,
    },
    overallDecision,
  }
}

function formatZoneOverrides(
  zone: ZoneParams
): ReadonlyArray<NiiOverrideDetail> | undefined {
  const overrides = listZoneOverrides(zone)
  if (overrides.length === 0) return undefined

  return overrides.map(
    ({ field, overrideValue, calculatedValue, justification }) => ({
      label: field.label,
      calculated:
        calculatedValue === undefined
          ? undefined
          : formatStoredZoneOverrideValue(field, calculatedValue),
      value: formatStoredZoneOverrideValue(field, overrideValue),
      justification: justification?.text,
      by: justification?.justifiedBy,
      at: justification && formatNiiSignoffTimestamp(justification.justifiedAt),
    })
  )
}

export function buildZoneOverridesTable(
  overrides: ReadonlyArray<NiiOverrideDetail>
): PdfTableData {
  return {
    headers: ['Field', 'Calculated', 'Override', 'Justification'],
    columnWidths: [0.2, 0.15, 0.15, 0.5],
    rows: overrides.map((override) => {
      const overriddenBy = formatOverriddenBy(override)
      return {
        cells: [
          override.label,
          override.calculated ?? '—',
          override.value,
          override.justification
            ? `${override.justification}${overriddenBy && `\n— ${overriddenBy}`}`
            : '—',
        ],
      }
    }),
  }
}

function buildRbaTable(
  rbaInfo: RbaInformation | undefined,
  strategy: NiiStrategy | undefined
): PdfTableData | undefined {
  const strategyType = getEffectiveStrategyType(strategy)
  if (!strategyType || strategyType === InspectionStrategy.C) return undefined

  const rows: PdfTableRow[] = []
  const density = rbaInfo?.density_of_degradation ?? 'medium'
  rows.push({
    cells: [
      'Density of Degradation (Coverage Modifier)',
      capitalize(density),
      rbaInfo?.density_justification ?? '—',
    ],
  })

  if (strategyType === InspectionStrategy.B) {
    const accuracy = rbaInfo?.measurement_accuracy ?? 'medium'
    rows.push({
      cells: [
        'Measurement Strategy (Measurement Accuracy)',
        capitalize(accuracy),
        rbaInfo?.measurement_accuracy_justification ?? '—',
      ],
    })

    const tolerance = rbaInfo?.tolerance_of_degradation ?? 'medium'
    rows.push({
      cells: [
        'Tolerance of Degradation',
        capitalize(tolerance),
        rbaInfo?.tolerance_justification ?? '—',
      ],
    })

    const homogeneity = rbaInfo?.spatial_homogeneity ?? 'medium'
    rows.push({
      cells: [
        'Homogeneity of Degradation (Spatial Homogeneity)',
        capitalize(homogeneity),
        rbaInfo?.spatial_homogeneity_justification ?? '—',
      ],
    })
  }

  return {
    headers: ['Variable', 'Value', 'Justification'],
    columnWidths: [0.42, 0.16, 0.42],
    rows,
  }
}

function buildCoverageRequirementsTable(
  coverage: CoverageCalculation | undefined
): PdfTableData | undefined {
  if (!coverage) return undefined

  const values: Record<string, string> = {
    coverageModifier: coverage.f_cov?.toFixed(1) ?? '—',
    consequenceOfFailure: coverage.f_cons?.toFixed(1) ?? '—',
    zoneArea: coverage.f_zone?.toFixed(2) ?? '—',
    baseCoverage: String(coverage.base_coverage_percent ?? '—'),
    measurementAccuracy: coverage.f_acc?.toFixed(1) ?? '—',
    spatialHomogeneity: coverage.f_sh?.toFixed(2) ?? '—',
    toleranceOfDegradation: coverage.f_lc?.toFixed(1) ?? '—',
    coverageRequired:
      coverage.required_coverage_percent !== undefined
        ? `${coverage.required_coverage_percent.toFixed(2)} %`
        : '—',
    coverageArea:
      coverage.required_coverage_area_sq_m !== undefined
        ? `${coverage.required_coverage_area_sq_m.toFixed(2)} m²`
        : '—',
  }

  // Wide coverage table is awkward in PDF; render as label/value pairs instead.
  return {
    headers: ['Parameter', 'Value'],
    columnWidths: [0.55, 0.45],
    rows: COVERAGE_COLUMNS.map((col) => ({
      cells: [col.title, values[col.dataIndex] ?? '—'],
    })),
  }
}

function buildCoverageBreakdown(
  zone: ZoneParams,
  coverage: CoverageCalculation,
  strategyType: InspectionStrategy,
  confidence: 'low' | 'med' | 'high'
): NiiCoverageBreakdown {
  const zoneArea = getEffectiveZoneArea(zone)

  if (strategyType === InspectionStrategy.C) {
    return {
      title: 'Required Coverage - Type C',
      resultPercent: '100%',
      resultSubtext: `${zoneArea.toFixed(2)} m² total zone area — Type C always requires full coverage`,
      formulaLines: [],
      modifiers: [],
    }
  }

  const raw = coverage.raw_calculated_percent
  const wasClamped =
    raw !== undefined &&
    coverage.required_coverage_percent !== undefined &&
    raw !== coverage.required_coverage_percent

  const typeBLimits =
    strategyType === InspectionStrategy.B
      ? getTypeBLimits(confidence)
      : undefined

  const clampedSubtext = !wasClamped
    ? ''
    : strategyType === InspectionStrategy.A
      ? ' (clamped to minimum 2.5%)'
      : typeBLimits
        ? ` (clamped to ${raw! < typeBLimits.min ? `floor ${typeBLimits.min}%` : `ceiling ${typeBLimits.max}%`})`
        : ''

  const formulaLines: string[] = []
  if (strategyType === InspectionStrategy.A) {
    formulaLines.push('Formula: CR = max(2.5%, FCOV × FCONS × FZONE × C1)')
    formulaLines.push(
      `= max(2.5%, ${coverage.f_cov?.toFixed(2)} × ${coverage.f_cons?.toFixed(2)} × ${coverage.f_zone?.toFixed(2)} × 7.5%)`
    )
    formulaLines.push(`= max(2.5%, ${raw?.toFixed(2)}%)`)
    formulaLines.push(
      `= ${coverage.required_coverage_percent?.toFixed(2)}%${
        wasClamped ? ` ← clamped from ${raw?.toFixed(2)}% to minimum 2.5%` : ''
      }`
    )
  } else if (strategyType === InspectionStrategy.B) {
    formulaLines.push(
      'Formula: CR = FCOV × FACC × FLC × FCONS × FSH × FZONE × C1'
    )
    formulaLines.push(
      `= ${coverage.f_cov?.toFixed(2)} × ${coverage.f_acc?.toFixed(2)} × ${coverage.f_lc?.toFixed(2)} × ${coverage.f_cons?.toFixed(2)} × ${coverage.f_sh?.toFixed(2)} × ${coverage.f_zone?.toFixed(2)} × 25%`
    )
    if (wasClamped && typeBLimits) {
      formulaLines.push(
        `= ${raw?.toFixed(2)}% ← clamped to ${
          raw! < typeBLimits.min
            ? `floor ${typeBLimits.min}%`
            : `ceiling ${typeBLimits.max}%`
        }`
      )
      formulaLines.push(
        `= ${coverage.required_coverage_percent?.toFixed(2)}% (confidence range: ${typeBLimits.min}% – ${typeBLimits.max}%)`
      )
    } else {
      formulaLines.push(`= ${coverage.required_coverage_percent?.toFixed(2)}%`)
    }
  }

  const modifiers: string[] = [`FCOV = ${coverage.f_cov?.toFixed(2)}×`]
  if (strategyType === InspectionStrategy.B) {
    modifiers.push(
      `FACC = ${coverage.f_acc?.toFixed(2)}×`,
      `FLC = ${coverage.f_lc?.toFixed(2)}×`,
      `FSH = ${coverage.f_sh?.toFixed(2)}×`
    )
  }
  modifiers.push(
    `FCONS = ${coverage.f_cons?.toFixed(2)}×`,
    `FZONE = ${coverage.f_zone?.toFixed(2)}×`
  )

  return {
    title: `Required Coverage - Type ${strategyType}`,
    resultPercent: `${coverage.required_coverage_percent?.toFixed(1)}%`,
    resultSubtext: `${coverage.required_coverage_area_sq_m?.toFixed(2)} m² of ${zoneArea.toFixed(2)} m² total zone area${clampedSubtext}`,
    formulaLines,
    modifiers,
  }
}

function buildZoneDetail(
  zone: ZoneParams,
  zoneIndex: number,
  cofModifier: number,
  confidence: 'low' | 'med' | 'high'
): NiiZoneDetail {
  const coverage = calculateRequiredCoverage(zone, cofModifier, confidence)
  const strategyType = getEffectiveStrategyType(zone.nii_strategy)

  const detail: NiiZoneDetail = {
    title: zone.title || zone.description || `Zone ${zoneIndex + 1}`,
    color: resolveZoneColor(zone, zoneIndex),
    zoneIndex,
    damageMechanisms: buildDamageMechanismsTable(zone),
    designAndFunction: buildDesignAndFunctionTable(zone),
    strategy: buildStrategySection(zone.nii_strategy),
    rba: buildRbaTable(zone.rba_info, zone.nii_strategy),
    coverageRequirements: buildCoverageRequirementsTable(coverage),
    overrides: formatZoneOverrides(zone),
  }

  if (coverage && strategyType) {
    detail.coverageBreakdown = buildCoverageBreakdown(
      zone,
      coverage,
      strategyType,
      confidence
    )
  }

  return detail
}

export function buildNiiZoneAnalysisContent(
  zones: ReadonlyArray<ZoneParams>,
  cof: ConsequenceOfFailure,
  confidence: 'low' | 'med' | 'high',
  zoneBreakdownJustification?: string
): NiiZoneAnalysis {
  const cofModifier = getCofModifier(cof.level)
  const overviewRows = zones.map((zone, index) =>
    buildZoneOverviewRow(zone, index, cofModifier, confidence)
  )

  return {
    zoneBreakdownJustification: zoneBreakdownJustification || undefined,
    overview:
      overviewRows.length > 0
        ? {
            headers: [
              'Zone',
              'Zone Description',
              'Damage Mechanisms',
              'Coverage %',
              'Coverage',
            ],
            columnWidths: [0.08, 0.28, 0.28, 0.18, 0.18],
            rows: overviewRows.map((row) => ({
              cells: [
                String(row.zone),
                row.description,
                row.damageMechanisms,
                row.coveragePercent,
                row.coverageArea,
              ],
            })),
          }
        : undefined,
    zones: zones.map((zone, index) =>
      buildZoneDetail(zone, index, cofModifier, confidence)
    ),
  }
}
